import $ from "jquery";

/**
 * Every push carries a `unique_event_id` (deduplication for server-side tagging). The
 * generator is defined by the inline pageview script in the head (lib/wp/datalayer.php).
 */
const SbPushToDataLayer = (data) => {
    window.dataLayer = window.dataLayer || [];

    const payload = { ...data };
    if (payload.event && !payload.unique_event_id && window.SbUniqueEventId) {
        payload.unique_event_id = window.SbUniqueEventId();
    }

    if (DEBUG) {
        console.debug("[Tracking] - PUSH", payload);
    }
    window.dataLayer.push(payload);
};

/**
 * GA4 e-commerce event. The previous `ecommerce` object is cleared first, otherwise GTM
 * merges its items into this event.
 */
const SbPushEcommerceEvent = (event, ecommerce, data = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });

    SbPushToDataLayer({ event, ...data, ecommerce });
};

/**
 * Item from a `data-sb-item` attribute (rendered by sb_datalayer_item_json()).
 */
const SbReadItem = (element) => {
    try {
        return JSON.parse(element.getAttribute("data-sb-item"));
    } catch (e) {
        return null;
    }
};

// Expose to window so that other components can utilise it as well (preact-decisions and cxsForm)
window.SbPushToDataLayer = SbPushToDataLayer;
window.SbPushEcommerceEvent = SbPushEcommerceEvent;
window.SbReadItem = SbReadItem;

/**
 * Attribution Tracking - Capture UTM parameters and click IDs
 * Stores first-touch and last-touch attribution in cookies (90 days)
 *
 * Elk bezoek met campagneparameters is één complete set. Tot september 2026 werd per parameter
 * bijgewerkt: een Bing-klik zonder `utm_id` liet het `utm_id` en de gclid van een eerdere
 * Google-klik in last touch staan, en een msclkid kwam ook in first touch terecht. Nu vervangt
 * een nieuw bezoek de hele last-touch-set (wat in de URL ontbreekt, wordt leeg) en wordt first
 * touch alleen geschreven als er nog geen first touch is.
 *
 * Bezoeken zonder parameters (organisch, direct, interne navigatie) laten beide sets staan.
 * De formuliervelden worden pas bij het verzenden gevuld, zie lib/wp/gravity-forms.php.
 */
const SbCaptureAttributionData = () => {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);

    // Parameters to track. Keep in sync with voja_get_attribution_params() in
    // lib/wp/gravity-forms.php.
    const attributionParams = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "utm_id",
        "gclid",
        "fbclid",
        "msclkid",
    ];

    // Check if any attribution parameters exist in URL
    let hasAttributionParams = false;
    const attributionData = {};

    attributionParams.forEach((param) => {
        const value = urlParams.get(param);
        if (value) {
            hasAttributionParams = true;
            attributionData[param] = value;
        }
    });

    // Only proceed if we have attribution parameters
    if (!hasAttributionParams) {
        return;
    }

    // Cookie settings (90 days)
    const cookieDuration = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds
    const expiryDate = new Date(Date.now() + cookieDuration);
    const cookieSuffix = `; path=/; SameSite=Lax${
        window.location.protocol === "https:" ? "; Secure" : ""
    }`;

    // Helper function to get cookie
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop().split(";").shift();
        }
        return null;
    };

    // Helper function to set cookie
    const setCookie = (name, value) => {
        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expiryDate.toUTCString()}${cookieSuffix}`;
    };

    const deleteCookie = (name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${cookieSuffix}`;
    };

    // Schrijft de hele set voor één touch: aanwezige parameters krijgen hun waarde, de rest
    // wordt gewist, zodat er nooit waarden van twee bezoeken door elkaar staan.
    const writeTouch = (type) => {
        attributionParams.forEach((param) => {
            const cookieName = `sb_${type}_${param}`;
            if (attributionData[param]) {
                setCookie(cookieName, attributionData[param]);
            } else {
                deleteCookie(cookieName);
            }
        });

        if (DEBUG) {
            console.debug(`[Attribution] ${type}-touch`, attributionData);
        }
    };

    // First touch: de landingspagina wordt altijd met de set meegeschreven, dus zonder dat
    // cookie is er nog geen first touch.
    if (!getCookie("sb_first_landing_page")) {
        writeTouch("first");
        setCookie("sb_first_landing_page", window.location.href);

        if (document.referrer) {
            setCookie("sb_first_referrer", document.referrer);
        } else {
            deleteCookie("sb_first_referrer");
        }
    }

    // Last touch (always replace the whole set)
    writeTouch("last");
};

// Run attribution tracking immediately (before DOM ready to ensure cookies are set ASAP)
SbCaptureAttributionData();

/**
 * form_submit event (server-confirmed)
 * On a genuine submission the server (gform_after_submission) sets a one-time token
 * cookie. Exchange it for its payload via REST and push `form_submit` to the dataLayer.
 * Bot-resistant: only a real, GF-validated submission issues a token, so hitting the
 * thank-you page URL directly fires nothing. See lib/wp/gf-form-submit-tracking.php.
 */
const SB_FORM_SUBMIT_COOKIE = "sb_form_submit";

const SbReadCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop().split(";").shift();
    }
    return null;
};

const SbDeleteCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${
        window.location.protocol === "https:" ? "; Secure" : ""
    }`;
};

const SbEmitFormSubmitEvent = () => {
    const token = SbReadCookie(SB_FORM_SUBMIT_COOKIE);
    if (!token) {
        return;
    }

    // Delete immediately so a page refresh can't refire even if the request fails.
    SbDeleteCookie(SB_FORM_SUBMIT_COOKIE);

    fetch(
        `${BASE_URL}/wp-json/sb/v1/form-submit-event?token=${encodeURIComponent(token)}`,
        { credentials: "same-origin" },
    )
        .then((res) => (res.status === 200 ? res.json() : null))
        .then((payload) => {
            // One event, or a list (a booking pushes to_payment, then purchase).
            [].concat(payload || []).forEach(({ ecommerce, ...data }) => {
                if (!data.event) {
                    return;
                }

                if (ecommerce) {
                    SbPushEcommerceEvent(data.event, ecommerce, data);
                } else {
                    SbPushToDataLayer(data);
                }

                if (data.event === "purchase" && window.SbClearCheckout) {
                    window.SbClearCheckout(ecommerce.items?.[0]?.item_id);
                }
            });
        })
        .catch(() => {});
};

// AJAX forms (the newsletter) show their confirmation without a pageload; the token cookie
// arrived with that response.
$(document).on("gform_confirmation_loaded", SbEmitFormSubmitEvent);

$(() => {
    // form_submit (server-confirmed, fires on the page after a genuine submission)
    SbEmitFormSubmitEvent();

    // Een AJAX-formulier (de bestemmingsgids) toont zijn bevestiging zonder nieuwe pageview; het
    // token-cookie staat dan al, dus lees het zodra de bevestiging er is.
    $(document).on("gform_confirmation_loaded", SbEmitFormSubmitEvent);

    // `pageview` is pushed inline in the head, before GTM (lib/wp/datalayer.php).

    // Search
    if (PAGE_TEMPLATE === "search") {
        // Get ?s query string
        const searchQuery = new URLSearchParams(window.location.search).get(
            "s",
        );

        SbPushToDataLayer({
            event: "view_search_results",
            tag_name: "GA4 - Event - Zoeken",
            search_term: searchQuery,
        });
    }
});
